import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../auth';
import { UserRole } from '../../types';
import { ChangePasswordModal } from '../../components/ChangePasswordModal';
import {
  Menu,
  X,
  Home,
  Users,
  Store,
  CreditCard,
  FileText,
  Settings,
  LogOut,
  ChevronDown,
  DollarSign,
  ShoppingCart,
  BarChart3,
  List,
  Award,
  Ban,
  Trophy,
  Wallet,
  UserCircle,
  Building2,
  ClipboardList,
  Calculator,
  Ticket,
  Printer,
  Smartphone,
  Lock,
  KeyRound,
  ClipboardCheck,
} from 'lucide-react';
import { Activity } from 'lucide-react';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  children?: { label: string; path: string }[];
}

// Navegación por rol
const navigationByRole: Record<UserRole, NavItem[]> = {
  ADMIN: [
    { label: 'Dashboard', path: '/admin', icon: <Home size={20} /> },
    { label: 'Bancas', path: '/admin/bancas', icon: <Building2 size={20} /> },
    { label: 'Usuarios', path: '/admin/usuarios', icon: <Users size={20} /> },
    { label: 'Suscripciones', path: '/admin/suscripciones', icon: <CreditCard size={20} /> },
    { label: 'Pagos', path: '/admin/pagos', icon: <DollarSign size={20} /> },
    { label: 'Seguridad', path: '/admin/seguridad', icon: <Lock size={20} /> },
  ],
  BANCA: [
    { label: 'Dashboard', path: '/banca/dashboard', icon: <Home size={20} /> },
    { label: 'Sorteos', path: '/banca/sorteos', icon: <Trophy size={20} /> },
    { label: 'Listas', path: '/banca/listas', icon: <ClipboardList size={20} /> },
    { label: 'Vendedores', path: '/banca/vendedores', icon: <Users size={20} /> },
    { label: 'Numeros Premiados', path: '/banca/premios', icon: <Award size={20} /> },
    { label: 'Restringidos', path: '/banca/restringidos', icon: <Ban size={20} /> },
    { label: 'Ganadores', path: '/banca/ganadores', icon: <Trophy size={20} /> },
    { label: 'Saldos', path: '/banca/saldos', icon: <Wallet size={20} /> },
    { label: 'Reportes', path: '/banca/reportes', icon: <BarChart3 size={20} /> },
    { label: 'Sorteos por Local', path: '/banca/locales-sorteos', icon: <Store size={20} /> },
    { label: 'Auditoría', path: '/banca/auditoria', icon: <ClipboardCheck size={20} /> },
    { label: 'Analítica', path: '/banca/analitica', icon: <Activity size={20} /> },
  ],
  LOCAL: [
    { label: 'Dashboard', path: '/local/dashboard', icon: <Home size={20} /> },
    { label: 'Mi Local', path: '/local/locales', icon: <Store size={20} /> },
    { label: 'Vendedores', path: '/local/vendedores', icon: <Users size={20} /> },
    { label: 'Listas', path: '/local/listas', icon: <ClipboardList size={20} /> },
    { label: 'Créditos', path: '/vendedor/creditos', icon: <DollarSign size={20} /> },
    { label: 'Reglas', path: '/local/reglas', icon: <Settings size={20} /> },
    { label: 'Validar SINPE / Transfer.', path: '/local/validacion-sinpe', icon: <Smartphone size={20} /> },
    { label: 'Ganadores', path: '/local/ganadores', icon: <Trophy size={20} /> },

    { label: 'Caja Lotería', path: '/local/caja-loteria', icon: <Calculator size={20} /> },
    { label: 'Cierres de Caja', path: '/local/cierre-loterias', icon: <Lock size={20} /> },
    { label: 'Mi Saldo', path: '/local/saldos', icon: <Wallet size={20} /> },
    { label: 'Reportes', path: '/local/reportes', icon: <BarChart3 size={20} /> },
  ],
  VENDEDOR_CON_CAJA: [
    { label: 'POS', path: '/vendedor/pos', icon: <ShoppingCart size={20} /> },
    { label: 'Mi Caja', path: '/vendedor/caja', icon: <Wallet size={20} /> },
    { label: 'Mis Ventas', path: '/vendedor/mis-ventas', icon: <FileText size={20} /> },
    { label: 'Listas', path: '/vendedor/listas', icon: <List size={20} /> },
    { label: 'Créditos', path: '/vendedor/creditos', icon: <DollarSign size={20} /> },
    { label: 'Premios', path: '/vendedor/premios', icon: <Award size={20} /> },
    { label: 'Ganadores', path: '/vendedor/ganadores', icon: <Trophy size={20} /> },
    { label: 'Configuración de Ticket', path: '/vendedor/ticket-config', icon: <Printer size={20} /> },
  ],
  VENDEDOR_SIN_CAJA: [
    { label: 'POS', path: '/vendedor/pos', icon: <ShoppingCart size={20} /> },
    { label: 'Mis Ventas', path: '/vendedor/mis-ventas', icon: <FileText size={20} /> },
    { label: 'Listas', path: '/vendedor/listas', icon: <List size={20} /> },
    { label: 'Ganadores', path: '/vendedor/ganadores', icon: <Trophy size={20} /> },
  ],
};

export const AppLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [showChangePassword, setShowChangePassword] = useState(false);

  if (!user) return null;

  const navItems = navigationByRole[user.role] || [];
  const canChangePassword = ['ADMIN', 'BANCA', 'LOCAL'].includes(user.role);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleExpanded = (label: string) => {
    setExpandedItems(prev => 
      prev.includes(label) 
        ? prev.filter(i => i !== label)
        : [...prev, label]
    );
  };

  const isActive = (path: string) => location.pathname === path;
  const isActiveParent = (item: NavItem) => {
    if (item.children) {
      return item.children.some(c => location.pathname.startsWith(c.path));
    }
    return location.pathname.startsWith(item.path);
  };

  // Sidebar content
  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-100">
        <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center">
          <span className="text-white font-bold text-lg">T</span>
        </div>
        <div className="flex-1">
          <h1 className="font-bold text-gray-900">TiemPos</h1>
          <p className="text-xs text-gray-500">v2.0</p>
        </div>
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label="Cerrar menú"
        >
          <X size={20} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        <ul className="space-y-1">
          {navItems.map(item => (
            <li key={item.path}>
              {item.children ? (
                <div>
                  <button
                    onClick={() => toggleExpanded(item.label)}
                    className={`
                      w-full flex items-center justify-between px-3 py-2.5 rounded-lg
                      transition-colors text-sm font-medium
                      ${isActiveParent(item) 
                        ? 'bg-primary-50 text-primary-700' 
                        : 'text-gray-600 hover:bg-gray-100'
                      }
                    `}
                  >
                    <span className="flex items-center gap-3">
                      {item.icon}
                      {item.label}
                    </span>
                    <ChevronDown 
                      size={16} 
                      className={`transition-transform ${expandedItems.includes(item.label) ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {expandedItems.includes(item.label) && (
                    <ul className="mt-1 ml-9 space-y-1">
                      {item.children.map(child => (
                        <li key={child.path}>
                          <Link
                            to={child.path}
                            onClick={() => setSidebarOpen(false)}
                            className={`
                              block px-3 py-2 rounded-lg text-sm
                              ${isActive(child.path)
                                ? 'bg-primary-50 text-primary-700 font-medium'
                                : 'text-gray-600 hover:bg-gray-100'
                              }
                            `}
                          >
                            {child.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <Link
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg
                    transition-colors text-sm font-medium
                    ${isActive(item.path) 
                      ? 'bg-primary-50 text-primary-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                    }
                  `}
                >
                  {item.icon}
                  {item.label}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* User section */}
      <div className="border-t border-gray-100 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
            <UserCircle className="w-6 h-6 text-gray-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{user.nombre}</p>
            <p className="text-xs text-gray-500 truncate">{user.role.replace('_', ' ')}</p>
          </div>
        </div>
        {canChangePassword && (
          <button
            onClick={() => setShowChangePassword(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 mb-2 text-sm text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          >
            <KeyRound size={18} />
            Cambiar contraseña
          </button>
        )}
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
        >
          <LogOut size={18} />
          Cerrar sesión
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Mobile */}
      <aside 
        className={`
          fixed left-0 top-0 h-full w-72 bg-white z-50 shadow-xl
          transform transition-transform duration-300 ease-out lg:hidden
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <SidebarContent />
      </aside>

      {/* Sidebar - Desktop */}
      <aside className="hidden lg:fixed lg:left-0 lg:top-0 lg:h-full lg:w-64 lg:bg-white lg:border-r lg:border-gray-200 lg:flex lg:flex-col">
        <SidebarContent />
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2.5 text-gray-600 hover:bg-gray-100 rounded-lg lg:hidden min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <Menu size={24} />
            </button>

            <div className="flex-1 lg:flex-none" />

            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500 hidden sm:block">
                {new Date().toLocaleDateString('es-CR', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className={`p-4 lg:p-6 ${(user.role === 'VENDEDOR_CON_CAJA' || user.role === 'VENDEDOR_SIN_CAJA') ? 'pb-20 lg:pb-6' : ''}`}>
          <Outlet />
        </main>
      </div>

      {/* Bottom navigation for mobile POS */}
      {(user.role === 'VENDEDOR_CON_CAJA' || user.role === 'VENDEDOR_SIN_CAJA') && (
        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 lg:hidden z-30 safe-area-inset-bottom">
          <div className="flex justify-around">
            {navItems.slice(0, 4).map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex flex-col items-center gap-1 px-3 py-3 min-h-[56px] flex-1 justify-center
                  ${isActive(item.path)
                    ? 'text-primary-600'
                    : 'text-gray-500'
                  }
                `}
              >
                {item.icon}
                <span className="text-xs font-medium">{item.label}</span>
              </Link>
            ))}
          </div>
        </nav>
      )}

      {/* Modal de cambiar contraseña */}
      {canChangePassword && (
        <ChangePasswordModal
          isOpen={showChangePassword}
          onClose={() => setShowChangePassword(false)}
        />
      )}
    </div>
  );
};

export default AppLayout;
